import * as Sentry from "@sentry/vue";
import { apiService } from "api/apiService";
import {
  feedbackIcon,
  logoutIcon,
  supportIcon,
  toClassicIcon,
} from "common/defaultIcons";
import { useClassroomNavigationStore } from "classroom/stores";
import useHelpers from "composables/useHelpers.js";
import { acceptHMRUpdate, defineStore } from "pinia";
import { useAuthStore } from "core/auth/auth.store";
import { useAuthenticationStore } from "store/modules/authenticationStore";
import { useFaqStore } from "store/modules/faqStore";
import { useKnowledgeBaseStore } from "store/modules/knowledgeBaseStore";
import { useModuleStore } from "store/modules/moduleStore.js";
import { useNotificationStore } from "store/modules/notificationStore.js";
import { computed, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

/**
 * Menu Store
 *
 * This store manages the state and actions related to the menu items.
 */
export const useMenuStore = defineStore("menuItem", () => {
  const { userHasRole, checkRole, clearAuthStorage } = useHelpers();
  const moduleStore = useModuleStore();
  const { hasSettingEnabled } = moduleStore;

  const authenticationStore = useAuthenticationStore();
  const { processResults } = authenticationStore;

  const notificationStore = useNotificationStore();
  const { fetchUnseenNotificationsCount } = notificationStore;

  const collapsed = ref(
    localStorage.getItem("menu_collapsed") === "true" || false,
  );

  const router = useRouter();
  const route = useRoute();

  // Refs
  const unreadNotificationsCount = ref(0);
  const menuIsOpen = ref(false);
  const notificationInterval =
    import.meta.env?.VITE_NOTIFICATION_FETCH_INTERVAL ?? 300000;

  // Menu expansion state: key -> boolean (expanded or not)
  const menuExpansionState = reactive({});

  const menuWidth = computed(() => (collapsed.value ? 72 : 344));

  // Dynamic badges for menu items, keyed by route name
  const menuBadges = computed(() => {
    const authStore = useAuthStore();
    const badges = {};

    if (authStore.pendingAssessmentsCount > 0) {
      badges["teacherReviewTasks"] = {
        label: authStore.pendingAssessmentsCount,
        color: "red",
        textColor: "white",
      };
    }

    // Plans handed in and waiting on this teacher's verdict.
    if (authStore.pendingActionplanReviewsCount > 0) {
      badges["teacherActionplansReview"] = {
        label: authStore.pendingActionplanReviewsCount,
        color: "red",
        textColor: "white",
      };
    }

    if (authStore.openAssignmentsCount > 0) {
      badges["studentTasks"] = {
        label: authStore.openAssignmentsCount,
        color: "red",
        textColor: "white",
      };
    }

    // Requested plans plus handed-in-but-ungraded ones.
    if (authStore.openActionplansCount > 0) {
      badges["studentActionplansIndex"] = {
        label: authStore.openActionplansCount,
        color: "red",
        textColor: "white",
      };
    }

    // A voorbereiding still to fill in, or a gespreksverslag still to write.
    if (authStore.openConversationsCount > 0) {
      badges["studentConversations"] = {
        label: authStore.openConversationsCount,
        color: "red",
        textColor: "white",
      };
    }

    return badges;
  });

  const sentryFeedbackForm = ref(null);

  const openSentryFeedback = async () => {
    try {
      const feedback = Sentry.getFeedback?.();
      if (!feedback) {
        return;
      }

      if (!sentryFeedbackForm.value) {
        sentryFeedbackForm.value = await feedback.createForm();
        sentryFeedbackForm.value.appendToDom();
      }

      sentryFeedbackForm.value.open();
    } catch (e) {
      console.log("Failed to open Sentry feedback form", e);
    }
  };

  // Computed
  /**
   * Determines if the submenu should be hidden.
   * @returns {boolean}
   */
  const hideSubmenu = computed(() => {
    return route?.meta?.hideSubmenu || false;
  });

  const checkRoutes = (routes, section = "main") => {
    const sections = Array.isArray(section) ? section : [section];
    const items = [];

    for (const route of routes) {
      const meta = route.meta ?? {};
      const isInSection = sections.includes(meta.menuSection);

      if (
        isInSection &&
        meta.isMenuItem &&
        userHasRole(meta.roleRequired ?? []) &&
        (!meta.settingRequired || meta.settingRequired.some(hasSettingEnabled))
      ) {
        let newRoute = { ...route };
        if (route.children && meta.hideSidebarSubmenu !== true) {
          newRoute.children = checkRoutes(route.children, sections);
        } else if (meta.hideSidebarSubmenu === true) {
          newRoute.children = [];
        }
        items.push(newRoute);
      }
    }

    return items;
  };

  // MenuItems Computed
  /**
   * Computes the menu items based on the routes and user roles.
   * @returns {Array<Object>}
   */
  const menuItems = computed(() => {
    return checkRoutes(router.options.routes).sort(
      (a, b) => a.meta.menuOrder - b.meta.menuOrder,
    );
  });

  const organiseItems = computed(() => {
    return checkRoutes(router.options.routes, "organise").sort(
      (a, b) => a.meta.menuOrder - b.meta.menuOrder,
    );
  });

  const manageItems = computed(() => {
    return checkRoutes(router.options.routes, "manage").sort(
      (a, b) => a.meta.menuOrder - b.meta.menuOrder,
    );
  });

  const profileItems = computed(() => {
    return checkRoutes(router.options.routes, "profile").sort(
      (a, b) => a.meta.menuOrder - b.meta.menuOrder,
    );
  });

  const impersonatingItems = computed(() => {
    return checkRoutes(router.options.routes, "impersonating").sort(
      (a, b) => a.meta.menuOrder - b.meta.menuOrder,
    );
  });

  /**
   * Computes the help menu items.
   * @returns {Array<Object>}
   */
  const helpItems = computed(() => [
    {
      name: "helpForm",
      meta: {
        title: "Helpformulier",
      },
    },
    {
      href: "https://catwise.nl/training",
      meta: {
        title: "Trainingen",
      },
    },
    {
      href: "https://help.catwise.nl/",
      meta: {
        title: "Kennisbank",
      },
    },
    {
      name: "feedbackForm",
      meta: {
        title: "Feedback sturen naar Catwise",
      },
    },
  ]);

  const toggleCollapsed = () => {
    collapsed.value = !collapsed.value;

    localStorage.setItem("menu_collapsed", collapsed.value);
  };

  /**
   * Computes the tab menu items for the header.
   * Finds the deepest route with hasSubmenu: true in meta.
   * Falls back to matched[1] for legacy routes (e.g., manage routes).
   * @returns {Array<Object>}
   */
  const tabMenuItems = computed(() => {
    // New pattern: find the deepest route with hasSubmenu meta
    const submenuRoute = route?.matched?.findLast(
      (matchedRoute) => matchedRoute.meta?.hasSubmenu === true,
    );

    // Legacy fallback: use matched[1] for routes like manage
    const parentRoute = submenuRoute ?? route?.matched?.[1];

    if (!parentRoute?.children) {
      return [];
    }

    const tabMenuRoutes = [];

    parentRoute.children.forEach((childRoute) => {
      tabMenuChecklist(tabMenuRoutes, childRoute);
    });

    return tabMenuRoutes;
  });

  // Methods

  /**
   * Checks the routes for tab menu items.
   * @param {Array<Object>} tabMenuRoutes - The tab menu routes.
   * @param {Object} route - The route to check.
   */
  const tabMenuChecklist = (tabMenuRoutes, route) => {
    if (Array.isArray(route.children) && route.children.length > 0) {
      route.children.forEach((route) => tabMenuChecklist(tabMenuRoutes, route));
    }

    if (!userHasRole(route.meta?.roleRequired ?? [])) {
      return;
    }

    tabMenuRoutes.push(route);
  };

  /**
   * Fetches unread notifications at regular intervals.
   */
  const fetchUnreadNotificationsWithInterval = () => {
    fetchUnseenNotificationsCount();
    setInterval(fetchUnseenNotificationsCount, notificationInterval);
  };

  /**
   * Fetches unfinished tasks at regular intervals.
   */
  const fetchUnfinishedTasksWithInterval = () => {
    // Only students has unfinished tasks
    if (!checkRole("student")) {
      return;
    }
  };

  /**
   * Checks if a menu item has a dropdown child.
   * @param {Object} item - The menu item to check.
   * @returns {boolean}
   */
  const hasDropdownChild = (item) => {
    if (!item.children?.length) {
      return false;
    }

    item.children.some((child) => {
      if (!child.meta?.settingRequired?.some(hasSettingEnabled)) {
        return false;
      }
    });

    return item.children.some(
      (child) => child?.meta?.inDropdown && showSubItem(child),
    );
  };

  /**
   * Determines if a submenu item should be shown.
   * @param {Object} subItem - The submenu item to check.
   * @returns {boolean}
   */
  const showSubItem = (subItem) => {
    if (!subItem?.meta?.inDropdown) {
      return false;
    }

    if (subItem?.meta?.settingRequired) {
      return subItem?.meta?.settingRequired.some(hasSettingEnabled);
    }

    return true;
  };

  /**
   * Toggles the menu open or closed.
   */
  const toggleMenu = () => {
    menuIsOpen.value = !menuIsOpen.value;
  };

  /**
   * Closes the menu.
   */
  const closeMenu = () => {
    menuIsOpen.value = false;
  };

  /**
   * Logs the user out.
   */
  const logout = async () => {
    try {
      await apiService.post("/logout");
    } finally {
      useClassroomNavigationStore().$reset();
      clearAuthStorage();

      window.location.reload();
    }
  };

  /**
   * Logs in as a specified school.
   * @param {Object} school - The school to log in as.
   * @returns {Promise<void>}
   */
  const loginAsSchool = async (school = {}) => {
    const { id = null, name = "", username = "" } = school || {};

    if (!id) {
      throw new Error("School ID is required to login as school.");
    }

    localStorage.setItem("school_id", id);
    localStorage.setItem("school_name", name);
    localStorage.setItem("user_name", username);

    const formData = new FormData();
    formData.set("school_id", id);

    try {
      const { data } = await apiService.post("/api/login-as-school", formData);

      localStorage.removeItem("school_choices");

      processResults(data);
    } catch (error) {
      console.log("🚀 ~ loginAsSchool ~ error:", error);
      throw error;
    }
  };

  const collapseMenuItem = computed(() => {
    return {
      meta: {
        title: collapsed.value ? "Uitklappen" : "Inklappen",
        icon: collapsed.value
          ? "fa-regular fa-right-to-line"
          : "fa-regular fa-left-to-line",
      },
      action: toggleCollapsed,
    };
  });

  const sentryFeedbackMenuItem = computed(() => {
    return {
      meta: {
        title: "Probleem melden",
        icon: toClassicIcon(feedbackIcon),
      },
      action: () => {
        openSentryFeedback();
      },
    };
  });

  const supportMenuItem = {
    name: "knowledgeBase",
    meta: {
      title: "Ondersteuning",
      icon: toClassicIcon(supportIcon),
    },
    action: () => {
      const faqStore = useFaqStore();
      const knowledgeBaseStore = useKnowledgeBaseStore();
      faqStore.selectedCategoryUuid = null;
      knowledgeBaseStore.clearSearch();
      router.push({ name: "knowledgeBase" });
    },
  };

  const logoutMenuItem = {
    meta: {
      title: "Uitloggen",
      icon: toClassicIcon(logoutIcon),
    },
    action: logout,
  };

  /**
   * Get the expansion state for a menu item
   * @param {string} key - The unique key for the menu item
   * @returns {boolean|undefined} - Returns undefined if no state is stored, otherwise boolean
   */
  const isMenuItemExpanded = (key) => {
    return menuExpansionState[key];
  };

  /**
   * Set the expansion state for a menu item
   * @param {string} key - The unique key for the menu item
   * @param {boolean} expanded - Whether the menu item should be expanded
   */
  const setMenuItemExpanded = (key, expanded) => {
    if (!key) {
      return;
    }
    menuExpansionState[key] = expanded;
  };

  /**
   * Toggle the expansion state for a menu item
   * @param {string} key - The unique key for the menu item
   * @returns {boolean} - The new expansion state
   */
  const toggleMenuItemExpanded = (key) => {
    if (!key) {
      return false;
    }
    const newState = !menuExpansionState[key];
    menuExpansionState[key] = newState;
    return newState;
  };

  return {
    menuItems,
    profileItems,
    impersonatingItems,
    organiseItems,
    manageItems,
    helpItems,
    tabMenuItems,
    hideSubmenu,
    fetchUnreadNotificationsWithInterval,
    fetchUnfinishedTasksWithInterval,
    unreadNotificationsCount,
    toggleMenu,
    closeMenu,
    menuIsOpen,
    logout,
    loginAsSchool,
    hasDropdownChild,
    collapsed,
    toggleCollapsed,
    showSubItem,
    collapseMenuItem,
    sentryFeedbackMenuItem,
    supportMenuItem,
    logoutMenuItem,
    menuWidth,
    menuBadges,
    // Menu expansion state
    menuExpansionState,
    isMenuItemExpanded,
    setMenuItemExpanded,
    toggleMenuItemExpanded,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useMenuStore, import.meta.hot));
}
